import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Settings, Clock, Shield, Download, Upload, Database, AlertTriangle, CheckCircle, Pencil, Save, Trash2, Mail, Send, CalendarClock, Printer, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { backupApi, appSettingsApi, smtpApi, backupScheduleApi, checkLayoutApi, securityApi } from '../api';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import versionInfo from '../version.json';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

// Default timeout in minutes
const DEFAULT_TIMEOUT = 25;

export const getSessionTimeout = () => {
  const stored = localStorage.getItem('sessionTimeout');
  return stored ? parseInt(stored, 10) : DEFAULT_TIMEOUT;
};

export const setSessionTimeout = (minutes) => {
  localStorage.setItem('sessionTimeout', minutes.toString());
  // Dispatch event so IdleTimeout component can update
  window.dispatchEvent(new CustomEvent('sessionTimeoutChanged', { detail: minutes }));
};

export const SettingsPage = () => {
  const { sessionTimeout: authTimeout, setSessionTimeout: setAuthTimeout } = useAuth();
  const [timeout, setTimeoutVal] = useState(authTimeout);
  const [encryptionEnabled, setEncryptionEnabled] = useState(false);
  const [savingSecurity, setSavingSecurity] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [pendingRestoreData, setPendingRestoreData] = useState(null);
  const fileInputRef = useRef(null);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [purging, setPurging] = useState(false);
  const [copyrightText, setCopyrightText] = useState('');
  const [editingFooter, setEditingFooter] = useState(false);
  const [footerCopyrightInput, setFooterCopyrightInput] = useState('');

  // SMTP state
  const [smtp, setSmtp] = useState({ host: '', port: 587, username: '', password: '', from_email: '', from_name: '', use_tls: true });
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);

  // Backup schedule state
  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const [backupSchedule, setBackupSchedule] = useState({ enabled: false, days: [], time: '05:00' });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [sendingBackup, setSendingBackup] = useState(false);

  // Check layout state
  const DEFAULT_LAYOUT = {
    top_margin: 0.0, left_margin: 0.0, check_height: 3.5,
    stub1_height: 3.75, stub2_height: 3.75, micr_bottom: 0.25,
    check_body_top: 0.95, logo_max_height: 0.7, logo_max_width: 1.0, font_scale: 1.0,
  };
  const [checkLayout, setCheckLayout] = useState(DEFAULT_LAYOUT);
  const [savingLayout, setSavingLayout] = useState(false);

  useEffect(() => {
    const fetchAppSettings = async () => {
      try {
        const res = await appSettingsApi.get();
        setCopyrightText(res.data.copyright_text || '');
      } catch (e) {}
    };
    const fetchSmtp = async () => {
      try {
        const res = await smtpApi.get();
        setSmtp(res.data);
      } catch (e) {}
    };
    const fetchSchedule = async () => {
      try {
        const res = await backupScheduleApi.get();
        setBackupSchedule(res.data);
      } catch (e) {}
    };
    fetchAppSettings();
    fetchSmtp();
    fetchSchedule();
    const fetchSecurity = async () => {
      try {
        const res = await securityApi.get();
        setTimeoutVal(res.data.session_timeout);
        setEncryptionEnabled(res.data.encryption_enabled);
      } catch (e) {}
    };
    fetchSecurity();
    const fetchLayout = async () => {
      try {
        const res = await checkLayoutApi.get();
        setCheckLayout(prev => ({ ...prev, ...res.data }));
      } catch (e) {}
    };
    fetchLayout();
  }, []);

  const handleSaveFooter = async () => {
    try {
      const res = await appSettingsApi.update({ copyright_text: footerCopyrightInput });
      setCopyrightText(res.data.copyright_text || '');
      setEditingFooter(false);
      toast.success('Footer updated');
      window.dispatchEvent(new CustomEvent('footerSettingsChanged'));
    } catch (e) {
      toast.error('Failed to update footer');
    }
  };

  const handleSaveSmtp = async () => {
    setSavingSmtp(true);
    try {
      const res = await smtpApi.update(smtp);
      setSmtp(res.data);
      toast.success('SMTP settings saved');
    } catch (e) {
      toast.error('Failed to save SMTP settings');
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    try {
      const res = await smtpApi.test();
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SMTP test failed');
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const res = await backupScheduleApi.update(backupSchedule);
      setBackupSchedule(res.data);
      toast.success('Backup schedule saved');
    } catch (e) {
      toast.error('Failed to save schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleSendBackupNow = async () => {
    setSendingBackup(true);
    try {
      const res = await backupScheduleApi.sendNow();
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send backup');
    } finally {
      setSendingBackup(false);
    }
  };

  const toggleDay = (day) => {
    setBackupSchedule(prev => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter(d => d !== day) : [...prev.days, day]
    }));
  };

  const handleSaveLayout = async () => {
    setSavingLayout(true);
    try {
      const res = await checkLayoutApi.update(checkLayout);
      setCheckLayout(res.data);
      toast.success('Check layout saved');
    } catch (e) {
      toast.error('Failed to save layout');
    } finally {
      setSavingLayout(false);
    }
  };

  const handleResetLayout = async () => {
    setSavingLayout(true);
    try {
      const res = await checkLayoutApi.reset();
      setCheckLayout(res.data);
      toast.success('Check layout reset to defaults');
    } catch (e) {
      toast.error('Failed to reset layout');
    } finally {
      setSavingLayout(false);
    }
  };

  const handlePrintTestPage = () => {
    const L = checkLayout;
    const win = window.open('', '_blank');
    if (!win) { toast.error('Pop-up blocked. Allow pop-ups for this site.'); return; }
    win.document.write(`<!DOCTYPE html><html><head><title>Check Layout Test Page</title>
    <style>
      @page { size: 8.5in 11in; margin: 0; }
      body { margin: 0; padding: 0; font-family: Arial, sans-serif; font-size: 9px; color: #334155; }
      .page { width: 8.5in; height: 11in; position: relative; padding-top: ${L.top_margin}in; padding-left: ${L.left_margin}in; box-sizing: border-box; }
      .section { border: 1px dashed #94a3b8; position: relative; box-sizing: border-box; overflow: hidden; }
      .label { position: absolute; background: #0f172a; color: white; padding: 2px 8px; font-size: 8px; font-weight: bold; letter-spacing: 0.5px; z-index: 2; }
      .ruler-h { position: absolute; top: 0; left: 0; right: 0; height: 12px; background: repeating-linear-gradient(90deg, transparent, transparent 0.9375in, #cbd5e1 0.9375in, #cbd5e1 1in); }
      .ruler-v { position: absolute; top: 0; left: 0; bottom: 0; width: 12px; background: repeating-linear-gradient(0deg, transparent, transparent 0.9375in, #cbd5e1 0.9375in, #cbd5e1 1in); }
      .pos-marker { position: absolute; border: 1px solid; padding: 2px 6px; font-size: 7px; font-weight: 600; }
      .check-body { position: absolute; top: ${L.check_body_top}in; left: 0.3in; right: 0.3in; border: 1px dotted #3b82f6; padding: 4px 6px; }
      .micr-line { position: absolute; bottom: ${L.micr_bottom}in; left: 0.3in; right: 0.3in; border: 1px dotted #dc2626; padding: 2px 6px; text-align: center; font-family: monospace; }
      .logo-box { position: absolute; top: 4px; left: 4px; border: 1px dashed #22c55e; width: ${L.logo_max_width}in; height: ${L.logo_max_height}in; display: flex; align-items: center; justify-content: center; }
      .info { padding: 6px; font-size: 8px; color: #64748b; }
    </style>
    </head><body>
    <div class="page">
      <div class="section" style="height: ${L.check_height}in; background: #fefce8;">
        <div class="ruler-h"></div><div class="ruler-v"></div>
        <div class="label" style="top: 0; left: 0;">CHECK (${L.check_height}in)</div>
        <div class="logo-box"><span style="color:#22c55e;font-size:7px;">LOGO<br>${L.logo_max_width}" x ${L.logo_max_height}"</span></div>
        <div class="check-body">
          <span class="pos-marker" style="color:#3b82f6;border-color:#3b82f6;top:0;left:0;">CHECK BODY @ ${L.check_body_top}in from top</span>
          <div style="margin-top:20px;color:#94a3b8;">PAY TO THE ORDER OF _____________________________ $ ________</div>
          <div style="margin-top:8px;color:#94a3b8;">____________________________________________________________ DOLLARS</div>
        </div>
        <div class="micr-line">
          <span style="color:#dc2626;font-size:7px;">MICR LINE @ ${L.micr_bottom}in from bottom</span>
          <div style="font-size:10px;letter-spacing:2px;color:#94a3b8;margin-top:2px;">⑈000000⑈ ⑆000000000⑆ ⑈0000000000⑈</div>
        </div>
      </div>
      <div class="section" style="height: ${L.stub1_height}in; background: #f0fdf4;">
        <div class="ruler-h"></div>
        <div class="label" style="top: 0; left: 0;">STUB 1 / MAILING PANEL (${L.stub1_height}in)</div>
        <div class="info" style="margin-top:18px;">
          <p>This section contains the mailing address panel for #10 window envelopes.</p>
          <p style="margin-top:${L.stub1_height > 3.5 ? '40' : '20'}px;font-size:12px;text-align:center;color:#94a3b8;">
            [Payee Name]<br>[Address Line 1]<br>[City, State ZIP]
          </p>
        </div>
      </div>
      <div class="section" style="height: ${L.stub2_height}in; background: #eff6ff;">
        <div class="ruler-h"></div>
        <div class="label" style="top: 0; left: 0;">STUB 2 / YOUR RECORD (${L.stub2_height}in)</div>
        <div class="info" style="margin-top:18px;">
          <p>Retained copy with check details, payee, amount, and memo.</p>
        </div>
      </div>
      <div style="position:absolute;bottom:8px;left:8px;right:8px;text-align:center;font-size:7px;color:#94a3b8;">
        PayMasterGeneral Test Page — Top Margin: ${L.top_margin}in | Left Margin: ${L.left_margin}in | Font Scale: ${L.font_scale}x — Print this page, then compare alignment with your check stock
      </div>
    </div>
    </body></html>`);
    win.document.close();
    setTimeout(() => { win.print(); }, 300);
  };


  const handlePurgePayments = async () => {
    setPurging(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.delete(`${API}/api/purge-payments`);
      toast.success(`Deleted ${res.data.checks_deleted} checks and ${res.data.wires_deleted} wires. Reset ${res.data.llcs_reset} LLCs.`);
      setShowPurgeConfirm(false);
    } catch (e) {
      toast.error('Failed to purge payments');
    } finally {
      setPurging(false);
    }
  };

  const handleTimeoutChange = async (value) => {
    const newTimeout = parseInt(value, 10);
    setTimeoutVal(newTimeout);
    setSavingSecurity(true);
    try {
      await securityApi.update({ session_timeout: newTimeout });
      setAuthTimeout(newTimeout);
      localStorage.setItem('sessionTimeout', String(newTimeout));
      toast.success(`Session timeout set to ${newTimeout === 1 ? '1 minute (testing)' : `${newTimeout} minutes`}`);
    } catch (e) {
      toast.error('Failed to save session timeout');
    } finally {
      setSavingSecurity(false);
    }
  };

  const handleBackup = async () => {
    setBackingUp(true);
    try {
      const response = await backupApi.create();
      const backup = response.data;
      
      // Create and download the file
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().split('T')[0];
      a.download = `paymastergeneral-backup-${date}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully', {
        description: `${backup.users?.length || 0} users, ${backup.banks?.length || 0} banks, ${backup.llcs?.length || 0} LLCs, ${backup.payees?.length || 0} payees, ${backup.checks?.length || 0} checks, ${backup.wires?.length || 0} wires`
      });
    } catch (error) {
      console.error('Backup failed:', error);
      toast.error('Backup failed', { description: error.response?.data?.detail || 'Could not create backup' });
    } finally {
      setBackingUp(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result);
        
        // Validate backup structure
        if (!data.version || !data.created_at) {
          toast.error('Invalid backup file', { description: 'The file does not appear to be a valid backup' });
          return;
        }
        
        setPendingRestoreData(data);
        setShowRestoreConfirm(true);
      } catch (error) {
        toast.error('Invalid file', { description: 'Could not parse the backup file' });
      }
    };
    reader.readAsText(file);
    
    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  const handleRestore = async () => {
    if (!pendingRestoreData) return;
    
    setRestoring(true);
    setShowRestoreConfirm(false);
    
    try {
      const response = await backupApi.restore(pendingRestoreData);
      const result = response.data;
      
      toast.success('Backup restored successfully', {
        description: `Restored: ${result.restored.banks} banks, ${result.restored.llcs} LLCs, ${result.restored.payees} payees, ${result.restored.checks} checks, ${result.restored.wires} wires`
      });
      
      // Reload the page to refresh all data
      window.location.reload();
    } catch (error) {
      console.error('Restore failed:', error);
      toast.error('Restore failed', { description: error.response?.data?.detail || 'Could not restore backup' });
    } finally {
      setRestoring(false);
      setPendingRestoreData(null);
    }
  };

  const timeoutOptions = [
    { value: 1, label: '1 minute (Testing)' },
    { value: 5, label: '5 minutes' },
    { value: 10, label: '10 minutes' },
    { value: 15, label: '15 minutes' },
    { value: 25, label: '25 minutes (Default)' },
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 hour' },
  ];

  return (
    <div className="space-y-3 fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-lg font-semibold text-slate-900 tracking-tight">Settings</h1>
        <p className="text-slate-500 mt-1">Configure application preferences</p>
      </div>

      <div className="grid gap-6 max-w-2xl">
        {/* Security & Session Card */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Security
            </CardTitle>
            <CardDescription>
              Session timeout, encryption, and login protection settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="timeout">Inactivity Timeout</Label>
              <Select value={timeout.toString()} onValueChange={handleTimeoutChange} disabled={savingSecurity}>
                <SelectTrigger id="timeout" className="w-full" data-testid="timeout-select">
                  <SelectValue placeholder="Select timeout duration" />
                </SelectTrigger>
                <SelectContent>
                  {timeoutOptions.map(option => (
                    <SelectItem key={option.value} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-slate-500">
                You'll receive a warning 1 minute before the session expires.
              </p>
            </div>

            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                Auto-logout after {timeout} {timeout === 1 ? 'minute' : 'minutes'} of inactivity.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              <div className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-slate-50">
                <CheckCircle className={`w-4 h-4 flex-shrink-0 ${encryptionEnabled ? 'text-green-600' : 'text-slate-400'}`} />
                <div>
                  <p className="text-xs font-medium text-slate-700">Field Encryption</p>
                  <p className="text-[10px] text-slate-500">{encryptionEnabled ? 'Active — EIN, ABA, Account #' : 'Not configured'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-slate-50">
                <CheckCircle className="w-4 h-4 flex-shrink-0 text-green-600" />
                <div>
                  <p className="text-xs font-medium text-slate-700">Rate Limiting</p>
                  <p className="text-[10px] text-slate-500">5 attempts / 15 min</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-slate-50">
                <CheckCircle className={`w-4 h-4 flex-shrink-0 ${encryptionEnabled ? 'text-green-600' : 'text-slate-400'}`} />
                <div>
                  <p className="text-xs font-medium text-slate-700">Backup Encryption</p>
                  <p className="text-[10px] text-slate-500">{encryptionEnabled ? 'Encrypted at rest' : 'Unencrypted'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Backup Card */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-green-600" />
              Backup Data
            </CardTitle>
            <CardDescription>
              Download a complete backup of all your data including users, LLCs, banks, payees, checks, and wires
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <Database className="w-5 h-5 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-800">
                Backups include all your data including user accounts. Store backups securely as they contain sensitive financial information.
              </p>
            </div>
            <Button 
              onClick={handleBackup} 
              disabled={backingUp}
              className="w-full bg-green-600 hover:bg-green-700"
              data-testid="backup-btn"
            >
              {backingUp ? (
                <>Creating Backup...</>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download Backup
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Restore Card */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              Restore Data
            </CardTitle>
            <CardDescription>
              Restore your data from a previously downloaded backup file
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> Restoring a backup will replace ALL existing data. This action cannot be undone.
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="restore-file-input"
            />
            <Button 
              onClick={() => fileInputRef.current?.click()} 
              disabled={restoring}
              variant="outline"
              className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="restore-btn"
            >
              {restoring ? (
                <>Restoring...</>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Select Backup File
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* App Info Card */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-slate-600" />
              Application Info
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Version</span>
                <span className="text-slate-900 font-mono font-medium" data-testid="app-version">{versionInfo.version}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Build</span>
                <span className="text-slate-900 font-medium">{versionInfo.build_date}</span>
              </div>
              <p className="text-xs text-slate-400 pt-1">Version auto-increments on each build.</p>
            </div>
          </CardContent>
        </Card>

        {/* SMTP Configuration */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-slate-600" />
              SMTP Configuration
            </CardTitle>
            <CardDescription>Configure email server settings for notifications</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">SMTP Host</Label>
                  <Input value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} placeholder="smtp.example.com" className="mt-1" data-testid="smtp-host-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">SMTP Port</Label>
                  <Input type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: parseInt(e.target.value) || 587 })} placeholder="587" className="mt-1" data-testid="smtp-port-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Username</Label>
                  <Input value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} placeholder="user@example.com" className="mt-1" data-testid="smtp-username-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Password</Label>
                  <Input type="password" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} placeholder="••••••••" className="mt-1" data-testid="smtp-password-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">From Email</Label>
                  <Input value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} placeholder="noreply@example.com" className="mt-1" data-testid="smtp-from-email-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">From Name</Label>
                  <Input value={smtp.from_name} onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })} placeholder="PayMasterGeneral" className="mt-1" data-testid="smtp-from-name-input" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={smtp.use_tls}
                  onClick={() => setSmtp({ ...smtp, use_tls: !smtp.use_tls })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${smtp.use_tls ? 'bg-slate-900' : 'bg-slate-300'}`}
                  data-testid="smtp-tls-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${smtp.use_tls ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <Label className="text-sm">Use TLS Encryption</Label>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Test / Backup Email</Label>
                <Input value={smtp.test_email || ''} onChange={(e) => setSmtp({ ...smtp, test_email: e.target.value })} placeholder="admin@example.com" className="mt-1" data-testid="smtp-test-email-input" />
                <p className="text-xs text-slate-400 mt-1">Test emails and backup exports will be sent to this address</p>
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" onClick={handleSaveSmtp} disabled={savingSmtp} data-testid="save-smtp-btn">
                  <Save className="w-3.5 h-3.5 mr-1" /> {savingSmtp ? 'Saving...' : 'Save SMTP Settings'}
                </Button>
                <Button size="sm" variant="outline" onClick={handleTestSmtp} disabled={testingSmtp || !smtp.host} data-testid="test-smtp-btn">
                  <Send className="w-3.5 h-3.5 mr-1" /> {testingSmtp ? 'Sending...' : 'Send Test Email'}
                </Button>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                <span className="font-semibold">Note:</span> Test emails and backup exports are sent to the <span className="font-semibold">Test / Backup Email</span> address above. If empty, they go to the logged-in admin's email.
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Automated Backup Schedule */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-slate-600" />
              Automated Backup Schedule
            </CardTitle>
            <CardDescription>Configure when to automatically email database backups</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={backupSchedule.enabled}
                  onClick={() => setBackupSchedule(prev => ({ ...prev, enabled: !prev.enabled }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${backupSchedule.enabled ? 'bg-slate-900' : 'bg-slate-300'}`}
                  data-testid="backup-schedule-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${backupSchedule.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <Label className="text-sm font-medium">{backupSchedule.enabled ? 'Enabled' : 'Disabled'}</Label>
              </div>

              <div>
                <Label className="text-xs text-slate-500 mb-2 block">Days of the Week</Label>
                <div className="flex flex-wrap gap-2">
                  {DAYS.map(day => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(day)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors border ${backupSchedule.days.includes(day) ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                      data-testid={`backup-day-${day.toLowerCase()}`}
                    >
                      {day.slice(0, 3)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="w-40">
                <Label className="text-xs text-slate-500">Time (UTC)</Label>
                <Input
                  type="time"
                  value={backupSchedule.time}
                  onChange={(e) => setBackupSchedule(prev => ({ ...prev, time: e.target.value }))}
                  className="mt-1"
                  data-testid="backup-time-input"
                />
              </div>

              <div className="flex gap-2 pt-1">
                <Button size="sm" onClick={handleSaveSchedule} disabled={savingSchedule} data-testid="save-schedule-btn">
                  <Save className="w-3.5 h-3.5 mr-1" /> {savingSchedule ? 'Saving...' : 'Save Schedule'}
                </Button>
                <Button size="sm" variant="outline" onClick={handleSendBackupNow} disabled={sendingBackup || !smtp.host} data-testid="send-backup-now-btn">
                  <Send className="w-3.5 h-3.5 mr-1" /> {sendingBackup ? 'Sending...' : 'Send Backup Now'}
                </Button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                Backups are emailed to the <span className="font-semibold">Test / Backup Email</span> address configured in SMTP settings above. The email includes a JSON attachment with all data.
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Check Print Layout */}
        <Card className="border-slate-200" data-testid="check-layout-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-slate-600" />
              Check Print Layout
            </CardTitle>
            <CardDescription>Adjust check positioning for your printer and stock. All values are in inches unless noted.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { key: 'top_margin', label: 'Top Margin', desc: 'Shift entire page down', min: -0.5, max: 1.0, step: 0.05 },
                  { key: 'left_margin', label: 'Left Margin', desc: 'Shift entire page right', min: -0.5, max: 1.0, step: 0.05 },
                  { key: 'check_height', label: 'Check Height', desc: 'Height of check section', min: 2.5, max: 5.0, step: 0.05 },
                  { key: 'stub1_height', label: 'Stub 1 Height', desc: 'First stub section', min: 2.5, max: 5.0, step: 0.05 },
                  { key: 'stub2_height', label: 'Stub 2 Height', desc: 'Second stub / mail panel', min: 2.5, max: 5.0, step: 0.05 },
                  { key: 'micr_bottom', label: 'MICR Bottom Offset', desc: 'Distance from bottom edge', min: 0.05, max: 0.75, step: 0.01 },
                  { key: 'check_body_top', label: 'Check Body Top', desc: 'Top of payee/amount area', min: 0.5, max: 2.0, step: 0.05 },
                  { key: 'logo_max_height', label: 'Logo Max Height', desc: 'Maximum logo height', min: 0.3, max: 1.5, step: 0.05 },
                  { key: 'logo_max_width', label: 'Logo Max Width', desc: 'Maximum logo width', min: 0.5, max: 2.5, step: 0.1 },
                  { key: 'font_scale', label: 'Font Scale', desc: 'Multiplier (1.0 = normal)', min: 0.7, max: 1.5, step: 0.05 },
                ].map(({ key, label, desc, min, max, step }) => (
                  <div key={key}>
                    <Label className="text-xs text-slate-500">{label}</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        value={checkLayout[key]}
                        onChange={(e) => setCheckLayout(prev => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))}
                        min={min}
                        max={max}
                        step={step}
                        className="h-8 text-sm font-mono w-24"
                        data-testid={`layout-${key}`}
                      />
                      <input
                        type="range"
                        value={checkLayout[key]}
                        onChange={(e) => setCheckLayout(prev => ({ ...prev, [key]: parseFloat(e.target.value) }))}
                        min={min}
                        max={max}
                        step={step}
                        className="flex-1 h-1.5 accent-slate-600"
                      />
                    </div>
                    <span className="text-[10px] text-slate-400">{desc}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                <Button size="sm" onClick={handleSaveLayout} disabled={savingLayout} data-testid="save-layout-btn">
                  <Save className="w-3.5 h-3.5 mr-1" /> {savingLayout ? 'Saving...' : 'Save Layout'}
                </Button>
                <Button size="sm" variant="outline" onClick={handleResetLayout} disabled={savingLayout} data-testid="reset-layout-btn">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to Defaults
                </Button>
                <Button size="sm" variant="outline" onClick={handlePrintTestPage} data-testid="print-test-page-btn">
                  <Printer className="w-3.5 h-3.5 mr-1" /> Print Test Page
                </Button>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                Changes affect all checks for all LLCs. Print a test check after adjusting to verify alignment. Use "Top Margin" and "Left Margin" to compensate for printers that clip edges.
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-slate-600" />
              Footer Settings
            </CardTitle>
            <CardDescription>Copyright and version shown at the bottom of every page</CardDescription>
          </CardHeader>
          <CardContent>
            {editingFooter ? (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">Copyright Text</Label>
                  <Input
                    value={footerCopyrightInput}
                    onChange={(e) => setFooterCopyrightInput(e.target.value)}
                    placeholder="e.g. Copyright © 2026 Created by BillCasso"
                    className="mt-1"
                    data-testid="footer-copyright-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSaveFooter} data-testid="save-footer-btn">
                    <Save className="w-3.5 h-3.5 mr-1" /> Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingFooter(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Copyright</span>
                  <span className="text-slate-900 font-medium">{copyrightText || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Version</span>
                  <span className="text-slate-900 font-mono font-medium">{versionInfo.version} <span className="text-slate-400 text-xs">(auto-incremented)</span></span>
                </div>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => { setFooterCopyrightInput(copyrightText); setEditingFooter(true); }} data-testid="edit-footer-btn">
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit Footer
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Danger Zone - moved below */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-900">Delete All Checks & Wires</p>
                <p className="text-xs text-slate-500 mt-0.5">Permanently remove all checks and wire transfers, and reset check numbers.</p>
              </div>
              <AlertDialog open={showPurgeConfirm} onOpenChange={setShowPurgeConfirm}>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" data-testid="purge-payments-btn">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete All
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-5 h-5" />
                      Delete All Checks & Wires?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-base">
                      This will permanently delete <span className="font-semibold">all checks and wire transfers</span> and reset all LLC check numbers. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handlePurgePayments}
                      className="bg-red-600 hover:bg-red-700"
                      disabled={purging}
                      data-testid="confirm-purge-btn"
                    >
                      {purging ? 'Deleting...' : 'Yes, Delete Everything'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={showRestoreConfirm} onOpenChange={setShowRestoreConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm Restore
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  You are about to restore data from a backup. This will <strong>permanently delete</strong> all current data and replace it with the backup.
                </p>
                {pendingRestoreData && (
                  <div className="bg-slate-50 p-3 rounded-lg text-sm">
                    <p className="font-medium text-slate-700 mb-2">Backup Contents:</p>
                    <ul className="space-y-1 text-slate-600">
                      <li>• {pendingRestoreData.banks?.length || 0} banks</li>
                      <li>• {pendingRestoreData.llcs?.length || 0} LLCs</li>
                      <li>• {pendingRestoreData.payees?.length || 0} payees</li>
                      <li>• {pendingRestoreData.checks?.length || 0} checks</li>
                      <li>• {pendingRestoreData.wires?.length || 0} wires</li>
                    </ul>
                    <p className="mt-2 text-xs text-slate-500">
                      Backup created: {new Date(pendingRestoreData.created_at).toLocaleString()}
                    </p>
                  </div>
                )}
                <p className="text-red-600 font-medium">
                  This action cannot be undone. Are you sure you want to proceed?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore} className="bg-red-600 hover:bg-red-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              Yes, Restore Backup
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
